import React, { useEffect, useRef, useState } from 'react';

import Button from '@mui/material/Button';

import { inputTypeFormatters } from '@/components/DynamicForm/utils/functions';
import { formDataChangesFromSchema } from '@/components/DynamicForm/utils/dataMapper';
import { useAppContext, useFacilityContext } from '@/context';
import uiKits from '@/components/DynamicForm/FormElements/ui-kits/ui-kits';

import {
	AppHistoryEntry,
	DynamicFormConfig,
	DynamicFormEl,
	DynamicFormSchema,
	FormDataType,
	SchemaCall,
	UserSpaceFnPipe,
	ValidationTuple,
} from './types/DynamicFormTypes';
import { assocPath, createCallBodyByContentType, cssprefix, omit, path, pathOr, pipe } from './utils/utils';
import { smplTmplt } from './smplTmplt';
import { flattenFields, getFieldErrors, validateInput } from './validations';
import { parsePiped, remapObjectWith } from './utils/payloadParsing';
import InputRow from './InputRow';
import { mapJsonSchema, onSubmit } from './utils/onSubmit';
import StepIndicator from './StepIndicator';
import { subscribe, unsubscribe } from './events';

interface ApiCallResult {
	assign: string[];
	result: unknown;
	to?: string;
}

interface ProcessedApiListResponse {
	successes: number;
	intendedCalls: number;
	message: string;
	failedAt?: string;
}

interface GenerateCallsResult {
	errStr: string;
	nextFormDataState: FormDataType;
	nextSchemaState: DynamicFormSchema;
	proceed: boolean;
	success: boolean;
}

export interface DynamicFormProps {
	config?: DynamicFormConfig;
	defaultFormData?: FormDataType;
	defaultSchema?: DynamicFormSchema;
	overrodeSubmitHandler?: (data: FormDataType) => void;
	schemaJSON?: string;
	templatedPaths?: string[][];
}

const FOCUSABLE_CSS_SELECTOR = `button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), *[tabindex], *[contenteditable]`;

function findFirstFocusable(container: HTMLElement): HTMLElement | null {
	return container.querySelector(FOCUSABLE_CSS_SELECTOR);
}

const autoFocusFirstFocusable = (element: HTMLElement | null) => {
	if (element) {
		findFirstFocusable(element)?.focus();
	}
};

const scrollToRefTop = (element: HTMLElement | null) => {
	if (element) {
		element?.scrollIntoView({ behavior: 'smooth' });
	}
};

const scrollToFirstError = (errors: Record<string, string[]>) => {
	const firstErrorField = Object.keys(errors)[0];
	if (firstErrorField) {
		const element = document.getElementById(firstErrorField);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'center' });
			element.focus();
		}
	}
};

const createErrMsg = (errList: string[]): string => `
Sorry - there${errList.length > 1 ? ' are issues' : "'s an issue"} preventing progress:

${errList.join('\n')}

Give it a moment, then try proceeding to the next step again.
`;

interface PayloadOutcomes {
	hasPayload: (payload: Record<string, unknown>, filePayload: UserSpaceFnPipe, formData: FormDataType) => unknown;
	hasFilePayload: (payload: Record<string, unknown>, filePayload: UserSpaceFnPipe, formData: FormDataType) => unknown;
	hasNoPayload: () => null;
}

interface InitCallState {
	expiration: number;
	status: 'notasked' | 'pending' | 'success' | 'failure';
}

const payloadParseOutcomes: PayloadOutcomes = {
	hasFilePayload: (_, filePayload, formData) => parsePiped(filePayload, formData),
	hasPayload: (payload, _, formData) => remapObjectWith(payload, formData),
	hasNoPayload: () => null,
};

type FormDataUpdatePayload = [string, unknown, ValidationTuple[] | undefined, string[] | undefined, string | undefined];

type HistoryEntryToStateFn = (givenState: FormDataType, entry: AppHistoryEntry) => FormDataType;
const historyEntryToState: HistoryEntryToStateFn = (acc, [name, val, _, reset = []]) =>
	({
		...(reset.length ? omit(reset, acc) : acc),
		[name]: val,
	} as FormDataType);

export const appHistoryToState = (
	initState: FormDataType,
	appHistory: AppHistoryEntry[],
	begin: number | undefined,
	end: number | undefined
): FormDataType => appHistory.slice(begin || 0, end || appHistory.length).reduce(historyEntryToState, initState);

const parsePredicate = (whenConds: UserSpaceFnPipe, formData: FormDataType) => Boolean(parsePiped(whenConds, formData));

const navButtonClasses = (whenConds: UserSpaceFnPipe, formData: FormDataType) =>
	`${cssprefix}nav-button ${cssprefix}${parsePredicate(whenConds, formData) ? 'hide' : 'enable'}`;

export default function DynamicForm({
	config: formConfig = {},
	defaultFormData = {},
	defaultSchema = [] as DynamicFormSchema,
	schemaJSON = '{}',
	templatedPaths = [],
	overrodeSubmitHandler,
}: Readonly<DynamicFormProps>) {
	const { stylingClasses = 'w-full min-w-5 max-w-10 mx-auto my-0' } = formConfig || {};
	const { officeInfo, nearbyFacilities } = useFacilityContext() ?? {};
	const [loading, setLoading] = useState<boolean>(false);
	const [schema, setSchema] = useState<DynamicFormSchema>(defaultSchema);
	const initFormState = {
		...defaultFormData,
		...formConfig?.defaultFormData,
		facilityData: useFacilityContext(true),
		officeInfo,
		nearbyFacilities: nearbyFacilities ?? [],
	};
	const [formData, setFormData] = useState<FormDataType>(initFormState);
	const [currentStep, setCurrentStep] = useState<number>(0);
	const flattenedCurrentFields = flattenFields(schema?.[currentStep]?.fields ?? [], initFormState);
	const [currentFields, setCurrentFields] = useState<DynamicFormEl[]>(flattenedCurrentFields);
	const [formErrors, setFormErrors] = useState<Record<string, string[]>>(
		getFieldErrors(flattenedCurrentFields, formData)
	);

	let timer: ReturnType<typeof setTimeout>;
	const timerRunning = useRef(false);
	const appHistory = useRef<AppHistoryEntry[]>([]);
	const appHistoryIdx = useRef(0);

	const [initCallState, setInitCallState] = useState<InitCallState>({
		expiration: Date.now() + 4000,
		status: 'notasked',
	});
	const initCallRef = useRef(initCallState);

	const { config: appConfig } = useAppContext();

	const [interactedFields, setInteractedFields] = useState<Record<string, boolean>>({});
	const [completedTasks, setCompletedTasks] = useState<Record<string, number>>({});

	const topOfFormRef = useRef(null);

	const atLastStep = currentStep + 1 === (schema?.length ?? 0);
	const atFirstStep = currentStep === 0;
	const hasErrors = Object.keys(formErrors).length > 0;
	const {
		stepTitle,
		prevButton: {
			text: prevBtnText = 'Back',
			invalidWhen: prevBtnInvalidWhen = [['always', false]],
			size: prevBtnSize = 'M',
			variant: prevBtnVariant = 'tertiaryDefault',
		} = {},
		nextButton: {
			text: nextBtnText = 'Next',
			invalidWhen: nextBtnInvalidWhen = [['always', false]],
			size: nextBtnSize = 'M',
			variant: nextBtnVariant = 'secondaryDefault',
		} = {},
		submitButton: {
			text: submitBtnText = 'Submit',
			invalidWhen: submitBtnInvalidWhen = [['always', false]],
			size: submitBtnSize = 'M',
			variant: submitBtnVariant = 'primaryDefault',
		} = {},
	} = schema?.[currentStep] ?? {};

	const updateAccStates = () => {
		if (appHistory.current.length) {
			const nextFormState = appHistoryToState(
				formData,
				appHistory.current,
				appHistoryIdx.current,
				appHistory.current.length
			);
			const nextFlattendFields = flattenFields(schema?.[currentStep]?.fields ?? [], nextFormState);
			const nextErrors = getFieldErrors(nextFlattendFields, nextFormState);

			setFormData(nextFormState);
			setFormErrors(nextErrors);

			// clean-up
			appHistoryIdx.current = appHistory.current.length;
			clearTimeout(timer);
			timerRunning.current = false;
		}
	};

	const updateStates = (event: CustomEvent<FormDataUpdatePayload>) => {
		const [name, val, validations = [], reset = [], type = 'default'] = event.detail;
		const formattedValue = inputTypeFormatters[inputTypeFormatters[type] ? type : 'default'](val, formData[name]);

		// add all changes to the appHistory for completeness
		const nextEntry: AppHistoryEntry = [
			name,
			formattedValue,
			validateInput(formattedValue, validations, []),
			reset as PropertyKey[],
		];
		appHistory.current.push(nextEntry);

		// but most input types require immediate updates
		if (!['text', 'textarea', 'date', 'date-text-input', 'tel'].includes(type)) {
			const nextFormState = historyEntryToState(formData, nextEntry);
			const nextFlattendFields = flattenFields(schema?.[currentStep]?.fields ?? [], nextFormState);
			const nextErrors = getFieldErrors(nextFlattendFields, nextFormState);

			setFormData(nextFormState);
			setCurrentFields(nextFlattendFields);
			setFormErrors(nextErrors);
		} else if (!timerRunning.current) {
			// should only run when the type is in the group above
			// and when there isn't already a timer running
			timerRunning.current = true;
			timer = setTimeout(updateAccStates, 257);
		}
	};

	const handleSubmit = async (e: React.FormEvent): Promise<void> => {
		e.preventDefault();
		if (!hasErrors) {
			// We will change it soon when we try to make the Zenoti wrapper optional.
			// Trigger overrideSubmitHandler only in cases where we actually need to override the form behavior.
			if (formConfig?.onSubmit) {
				await onSubmit(schema, formData, { formConfig, appConfig });
			} else if (overrodeSubmitHandler) {
				overrodeSubmitHandler(formData);
			}
		}
	};

	const skipTo = (idx: number): void => {
		const nextFlattendFields = flattenFields(schema?.[idx]?.fields ?? [], formData);
		const nextErrors = getFieldErrors(nextFlattendFields, formData);

		setCurrentFields(nextFlattendFields);
		setFormErrors(nextErrors);
		setCurrentStep(idx);
	};

	const skipToFromEvt = (event: CustomEvent) => {
		const idx: number = event.detail as number;
		skipTo(idx);
	};

	const generateCalls = async (
		lookupPath: (string | number)[],
		argSchema?: DynamicFormSchema | DynamicFormConfig,
		argFormData?: FormDataType
	): Promise<GenerateCallsResult> => {
		const useFormData = argFormData || formData;
		const parsedSchema = JSON.parse(smplTmplt(schemaJSON, useFormData)) as DynamicFormSchema;

		let apiErrors: string[] = [];
		let proceed = true;
		let nextFormDataState: FormDataType = useFormData;
		let schemaUpdates: ApiCallResult[] = [];

		async function iterateApiCalls(calls: SchemaCall[]): Promise<ProcessedApiListResponse> {
			let currUrl = '';
			let successes = 0;
			try {
				for (const call of calls) {
					// reparse the call with the latest info from the previous iteration
					const parsedCall = JSON.parse(smplTmplt(JSON.stringify(call), nextFormDataState)) as SchemaCall;
					const {
						url,
						method,
						headers = { 'Content-Type': 'application/json' },
						payload,
						filePayload,
						onError = 'halt',
						adaptResultToSchema = {},
						adaptResultToFormData = {},
					} = parsedCall;
					currUrl = url;

					const remappedPayloadKey = [
						payload ? 'hasPayload' : false,
						filePayload ? 'hasFilePayload' : false,
					].reduce((acc, item) => item || acc, 'hasNoPayload') as keyof PayloadOutcomes;

					const remappedPayload = payloadParseOutcomes[remappedPayloadKey](
						payload ?? {},
						filePayload ?? [],
						Object.assign({}, useFormData, { schema: mapJsonSchema(schema) })
					);

					const response = await fetch(url, {
						method,
						headers,
						...createCallBodyByContentType(headers?.['Content-Type'], remappedPayload),
					} as RequestInit);

					if (!response.ok) {
						proceed = proceed && onError === 'proceed';
						throw new Error(`API call to ${url} failed with status ${response.status}`);
					}

					// only parse the response when the header specifies JSON
					if (headers?.['Content-Type'] === 'application/json') {
						/* eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment */
						const json = await response.json();

						const formDataUpdateObj = remapObjectWith(adaptResultToFormData, json);
						nextFormDataState = Object.assign(nextFormDataState, formDataUpdateObj);
						schemaUpdates = schemaUpdates.concat(
							Object.entries(remapObjectWith(adaptResultToSchema, json)).map(([key, val]) => ({
								assign: key.split('.'),
								result: val,
							}))
						);
					}
					successes += 1;
				}

				return { successes, intendedCalls: calls.length, message: 'All API calls completed successfully' };
			} catch (error) {
				apiErrors = apiErrors.concat(` - ${(error as Error).message}`);
				return {
					successes,
					intendedCalls: calls.length,
					message: error instanceof Error ? error.message : 'Unknown error occurred',
					failedAt: currUrl,
				};
			}
		}
		/* eslint-disable-next-line  @typescript-eslint/no-unsafe-assignment */
		const result = await iterateApiCalls(pathOr([], lookupPath, argSchema ?? parsedSchema) as SchemaCall[]);
		if (result.successes === result.intendedCalls) {
			const nextSchemaState: DynamicFormSchema = pipe(
				(schema: DynamicFormSchema) => schema,
				...templatedPaths.map(
					(templatedPath) => (schema: DynamicFormSchema) =>
						assocPath(templatedPath, path(templatedPath, parsedSchema), schema)
				),
				...schemaUpdates.map(
					({ assign, result }) =>
						(schema: DynamicFormSchema) =>
							assocPath(assign, result, schema)
				)
			)(schema);

			return {
				errStr: '',
				nextFormDataState,
				nextSchemaState,
				proceed,
				success: true,
			};
		}

		return {
			errStr: createErrMsg(apiErrors),
			nextFormDataState: useFormData,
			nextSchemaState: parsedSchema,
			proceed,
			success: false,
		};
	};

	const midStepCall = async (event: CustomEvent): Promise<void> => {
		const idx: number = event.detail as number;
		const { errStr, nextFormDataState, nextSchemaState, proceed, success } = await generateCalls([
			currentStep,
			'onUserInteraction',
			`${idx}`,
			'calls',
		]);

		if (success || (!success && proceed)) {
			const formDataChanges = formDataChangesFromSchema(
				schema?.[currentStep]?.onUserInteraction?.[`${idx}`]?.deriveData ?? [],
				formData
			);
			setFormData(Object.assign(nextFormDataState, formDataChanges));
			setSchema(nextSchemaState);
			setCurrentFields(flattenFields(nextSchemaState[currentStep]?.fields || [], nextFormDataState));
		} else {
			window.alert(errStr);
		}
	};

	const stepForward = async (): Promise<void> => {
		setLoading(true);
		const nextStep = currentStep + 1;

		const { errStr, nextFormDataState, nextSchemaState, proceed, success } = await generateCalls([
			currentStep,
			'onCompletion',
			'calls',
		]);

		if (success || (!success && proceed)) {
			const formDataChanges = formDataChangesFromSchema(
				schema?.[currentStep]?.onCompletion?.deriveData ?? [],
				formData
			);
			const flattenedFields = flattenFields(nextSchemaState[nextStep]?.fields || [], formDataChanges);

			setFormData(Object.assign(nextFormDataState, formDataChanges));
			setSchema(nextSchemaState);

			setCurrentFields(flattenedFields);
			setFormErrors(getFieldErrors(flattenedFields, nextFormDataState));
			setCurrentStep(nextStep);
			scrollToRefTop(topOfFormRef.current);
			autoFocusFirstFocusable(topOfFormRef.current);
		} else {
			window.alert(errStr);
		}

		setLoading(false);
	};

	const stepBack = (): void => {
		skipTo(currentStep - 1);
	};

	const updateCompletedTasks = (event: CustomEvent) => {
		const name = (event?.detail as string) || '';
		setCompletedTasks({ ...completedTasks, [name]: Date.now() });
	};

	const updateInteracted = (event: CustomEvent) => {
		const name = (event?.detail as string) || '';
		setInteractedFields({ ...interactedFields, [name]: true });
	};

	const markAllInteracted = () => {
		const currFieldsObj = currentFields.reduce(
			(acc, { name }) => (!name || acc?.[name] ? acc : { ...acc, [name]: true }),
			interactedFields
		);
		setInteractedFields({ ...interactedFields, ...currFieldsObj });
	};

	const cleanUpAfterInitCalls = () => {
		const rtnw = Date.now();
		if (initCallRef.current.expiration < rtnw && !['success'].includes(initCallRef.current.status)) {
			const schemaUpdates = Object.entries(
				remapObjectWith(pathOr({}, ['onInit', 'schemaCleanup'], formConfig), formData)
			).map(([key, val]) => ({
				assign: key.split('.'),
				result: val,
			}));
			const nextSchemaState = pipe(
				(schema: DynamicFormSchema) => schema,
				...schemaUpdates.map(
					({ assign, result }) =>
						(schema: DynamicFormSchema) =>
							assocPath(assign, result, schema)
				)
			)(schema);

			setSchema(nextSchemaState);
			setCurrentFields(flattenFields(nextSchemaState[0]?.fields || [], formData));
		}
	};

	useEffect(() => {
		// run only once after the initial render
		if ((path(['onInit', 'calls', 'length'], formConfig) as number) > 0) {
			setTimeout(cleanUpAfterInitCalls, 5000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const rtnw = Date.now();
		const nextFormDataState = { ...formData, officeInfo, nearbyFacilities: nearbyFacilities ?? [] };
		setFormData(nextFormDataState);

		if (
			nearbyFacilities &&
			nearbyFacilities?.length > 0 &&
			initCallRef.current.status === 'notasked' &&
			initCallRef.current.expiration > rtnw &&
			(path(['onInit', 'calls', 'length'], formConfig) as number) > 0
		) {
			const nextCallState: InitCallState = { ...initCallState, status: 'pending' };
			setInitCallState(nextCallState);
			initCallRef.current = nextCallState;
			generateCalls(['onInit', 'calls'], formConfig, nextFormDataState)
				.then(({ nextFormDataState, nextSchemaState, proceed, success }) => {
					const nextCallState: InitCallState = { ...initCallState, status: 'success' };
					setInitCallState(nextCallState);
					initCallRef.current = nextCallState;
					if (success || (!success && proceed)) {
						setFormData(nextFormDataState);
						setSchema(nextSchemaState);
						setCurrentFields(flattenFields(nextSchemaState[0]?.fields || [], nextFormDataState));
					}
					return { nextFormDataState, nextSchemaState, proceed, success };
				})
				.catch(() => {
					const nextCallState: InitCallState = { ...initCallState, status: 'failure' };
					setInitCallState(nextCallState);
					initCallRef.current = nextCallState;
					console.log('error during fetch init calls');
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nearbyFacilities]);

	// subscriptions
	useEffect(() => {
		subscribe('formDataUpdate', updateStates);

		return () => {
			unsubscribe('formDataUpdate', updateStates);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formData, formErrors, interactedFields]);

	/*
	 */
	useEffect(() => {
		subscribe('markInteracted', updateInteracted);

		return () => {
			unsubscribe('markInteracted', updateInteracted);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [interactedFields]);

	useEffect(() => {
		subscribe('taskCompletion', updateCompletedTasks);

		return () => {
			unsubscribe('taskCompletion', updateCompletedTasks);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [completedTasks]);

	useEffect(() => {
		const midStepCallWrapper = (event: CustomEvent) => {
			void midStepCall(event);
		};
		subscribe('midStepCall', midStepCallWrapper);

		return () => {
			unsubscribe('midStepCall', midStepCallWrapper);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formData, schema, currentFields]);

	useEffect(() => {
		subscribe('skipToStep', skipToFromEvt);

		return () => {
			unsubscribe('skipToStep', skipToFromEvt);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentFields, formErrors, currentStep, schema, formData]);

	const rootClasses = `${cssprefix}dynamic-form-layout ${stylingClasses}`.concat(
		formConfig?.stepIndicator?.position ? ` ${cssprefix}${formConfig.stepIndicator.position}` : ''
	);

	const loadingClasses = `${cssprefix}loading`.concat(loading ? '' : ` ${cssprefix}hide-loading`);

	return (
		<div className={rootClasses}>
			<StepIndicator {...(formConfig?.stepIndicator || {})} schema={schema} currentStep={currentStep} />
			{/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
			<form
				className={`${cssprefix}dynamic-form`}
				id="dynamic-form"
				data-testid="dynamic-form"
				onSubmit={(e) => {
					e.preventDefault();
				}}
				ref={topOfFormRef}
			>
				<fieldset className={`${cssprefix}fieldset`}>
					<legend className={`${cssprefix}legend`}>{stepTitle}</legend>
					<div className={`${cssprefix}inputs`}>
						{currentFields.map((field, fieldIndex) => (
							<InputRow
								key={`${field.name}_${fieldIndex}`}
								field={field}
								idx={fieldIndex}
								elLibrary={uiKits[formConfig?.uiKit ?? 'default']}
								interactedFields={interactedFields}
								formData={formData}
								formErrors={formErrors}
								completedTasks={completedTasks}
							/>
						))}
					</div>
				</fieldset>
				<nav className={`${cssprefix}form-nav`}>
					{!atFirstStep && (
						<Button
							className={navButtonClasses(prevBtnInvalidWhen as UserSpaceFnPipe, formData)}
							onClick={stepBack}
							size={prevBtnSize}
							variant={prevBtnVariant}
						>
							{prevBtnText}
						</Button>
					)}
					{!atLastStep ? (
						<Button
							className={navButtonClasses(nextBtnInvalidWhen as UserSpaceFnPipe, formData)}
							onClick={
								hasErrors
									? () => {
											void markAllInteracted();
											scrollToFirstError(formErrors);
									  }
									: () => void stepForward()
							}
							size={nextBtnSize}
							variant={nextBtnVariant}
						>
							{nextBtnText}
						</Button>
					) : (
						<Button
							type="submit"
							className={navButtonClasses(submitBtnInvalidWhen as UserSpaceFnPipe, formData)}
							onClick={
								hasErrors
									? (e) => {
											e.preventDefault();
											void markAllInteracted();
											scrollToFirstError(formErrors);
									  }
									: (e) => handleSubmit(e)
							}
							size={submitBtnSize}
							variant={submitBtnVariant}
						>
							{submitBtnText}
						</Button>
					)}
				</nav>
			</form>
			<div className={loadingClasses}>
				<span>Loading...</span>
			</div>
		</div>
	);
}
